import { useState, useEffect, useRef } from "react";
import { useWindowSize } from "../components/useWindowSize";
import { elements } from "../components/periodicElements";
import { Link, useNavigate } from "react-router-dom";
import {
  handleElementClick,
  renderElementVideo,
  renderColumn,
} from "../components/FunctionsElements";
import { useTranslation } from "react-i18next";

function TablePeriodic() {
  const windowSize = useWindowSize();
  const [selectedElement, setSelectedElement] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  // isMobileView para telas < 768px
  const isMobileView = screenWidth < 800;
  // isLargeScreen para telas >= 1024px
  const isLargeScreen = screenWidth >= 600;

  const [greenChemistryOpen, setGreenChemistryOpen] = useState(false);
  const [enablingSystemOpen, setEnablingSystemOpen] = useState(false);

  const greenChemistryRef = useRef(null);
  const enablingSystemRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
      // Se a tela ficar maior que 768, feche menus mobile (caso estivessem abertos)
      if (window.innerWidth >= 768) {
        setGreenChemistryOpen(false);
        setEnablingSystemOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        greenChemistryOpen &&
        greenChemistryRef.current &&
        !greenChemistryRef.current.contains(e.target)
      ) {
        setGreenChemistryOpen(false);
      }
      if (
        enablingSystemOpen &&
        enablingSystemRef.current &&
        !enablingSystemRef.current.contains(e.target)
      ) {
        setEnablingSystemOpen(false);
      }
    };

    if (isMobileView && (greenChemistryOpen || enablingSystemOpen)) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => document.removeEventListener("click", handleClickOutside);
  }, [greenChemistryOpen, enablingSystemOpen, isMobileView]);

  return (
    <section className="min-h-screen bg-background pt-24 md:pt-28 pb-12 w-full">
      {/* Background effects */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>

      {selectedElement ? (
        <div className="flex justify-center">
          {renderElementVideo(selectedElement, setSelectedElement, t, navigate)}
        </div> 
      ) : (
        <div className={`p-2 pb-6 mb-6 glass-card ${
            screenWidth < 1280 
              ? 'overflow-x-auto [-webkit-overflow-scrolling:touch]' 
              : 'overflow-x-hidden'
          }`}>
          {/* Mini Tutorial */}
          <div className="flex items-center justify-center gap-2 mb-4 mt-2">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-full">
              <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-xs text-primary font-medium">
                {t('ui.tutorialHint')}
              </span>
            </div>
          </div>

          {/* MENU PRINCIPAL */}
          <div className="mt-6 rounded-md min-w-max">
            <div
              className="flex items-center font-bold xl:gap-3 text-text-primary w-full min-w-[700px] px-4">
                <span className="w-1/3 mb-5 xs:text-[18px] xl:text-lg">
                    <h3 className="transition-all duration-300 hover:scale-105 hover:text-primary">
                      <Link to="/Humanitarian">
                        {t("Humanitarian.Humanitarian")}
                        <br />
                        {t("Humanitarian.Elements")}
                      </Link>
                    </h3>
                </span>

              {/* ============= MENU GREEN CHEMISTRY ============= */}
              {isMobileView ? (
                // Mobile
                <div
                  ref={greenChemistryRef}
                  className="relative w-3/4 xs:ml-4 xs:text-[18px] xl:text-lg transition-all duration-300 hover:scale-105 z-40"
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!greenChemistryOpen) {
                        setGreenChemistryOpen(true);
                      } else {
                        navigate("/GreenChemistry");
                      }
                    }}
                    className="w-full text-text-primary font-bold focus:outline-none hover:text-primary transition-colors"
                  >
                    <h3>{t("GreenChemistry.name")}</h3>
                  </button>
                  {greenChemistryOpen && (
                    <div
                      className="absolute glass-card text-text-primary shadow-glass rounded-xl xs:p-2 z-50 break-words"
                    >
                      <ul className="grid grid-cols-2 w-[100%] sm:grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                        <li>
                          <Link
                            to="/PreventWaste"
                            className="hover:text-PreventWaste"
                          >
                            {t("PreventWaste.title")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="/AtomEconomy"
                            className="hover:text-AtomEconomy"
                          >
                            {t("AtomEconomy.title")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="/LessHazardous"
                            className="hover:text-LessHazardous"
                          >
                            {t("LessHazardousSynthesis.title")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="/MolecularDesign"
                            className="hover:text-MolecularDesign"
                          >
                            {t("MolecularDesign.title")}
                          </Link>
                        </li>
                        <li>
                          <Link to="/Solvents" className="hover:text-Solvents">
                            {t("Solvents/AuxiliaryChemicals.title")}
                          </Link>
                        </li>
                        <li>
                          <Link to="/Energy" className="hover:text-Energy">
                            {t("Energy.title")}
                          </Link>
                        </li>
                        <li>
                          <Link to="/Renewable" className="hover:text-Renewable">
                            {t("RenewableFeedstocks.title")}
                          </Link>
                        </li>
                        <li>
                          <Link to="/Catalysis" className="hover:text-Catalysis">
                            {t("Catalysis.title")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="/Degradation"
                            className="hover:text-Degradation"
                          >
                            {t("Degradation.title")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="/Measurement"
                            className="hover:text-Measurement"
                          >
                            {t("MeasurementandAwareness.title")}
                          </Link>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                // Desktop
                <div className="relative w-2/3 group xl:text-lg transition-all duration-300 hover:scale-105 z-10">
                  <Link to="/GreenChemistry" className="text-text-primary hover:text-primary transition-colors">
                    <h3>{t("GreenChemistry.name")}</h3>
                  </Link>
                  <div
                    className="absolute hidden group-hover:block glass-card text-text-primary shadow-glass rounded-xl p-6 xs:p-2 z-50 break-words w-full"
                  >
                    <ul
                      className={`
                          grid 
                          gap-2 
                          text-base
                          w-100%
                          p-2
                          ${
                            isLargeScreen
                              ? "grid-cols-3" // Mais colunas em telas grandes
                              : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3"
                          }
                        `}
                    >
                      <li>
                        <Link
                          to="/PreventWaste"
                          className="hover:text-PreventWaste"
                        >
                          {t("PreventWaste.title")}
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/AtomEconomy"
                          className="hover:text-AtomEconomy"
                        >
                          {t("AtomEconomy.title")}
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/LessHazardous"
                          className="hover:text-LessHazardous"
                        >
                          {t("LessHazardousSynthesis.title")}
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/MolecularDesign"
                          className="hover:text-MolecularDesign"
                        >
                          {t("MolecularDesign.title")}
                        </Link>
                      </li>
                      <li>
                        <Link to="/Solvents" className="hover:text-Solvents">
                          {t("Solvents/AuxiliaryChemicals.title")}
                        </Link>
                      </li>
                      <li>
                        <Link to="/Energy" className="hover:text-Energy">
                          {t("Energy.title")}
                        </Link>
                      </li>
                      <li>
                        <Link to="/Renewable" className="hover:text-Renewable">
                          {t("RenewableFeedstocks.title")}
                        </Link>
                      </li>
                      <li>
                        <Link to="/Catalysis" className="hover:text-Catalysis">
                          {t("Catalysis.title")}
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/Degradation"
                          className="hover:text-Degradation"
                        >
                          {t("Degradation.title")}
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/Measurement"
                          className="hover:text-Measurement"
                        >
                          {t("MeasurementandAwareness.title")}
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              )}

              {/* ============= MENU ENABLING SYSTEM ============= */}
              {isMobileView ? (
                // Mobile
                <div
                  ref={enablingSystemRef}
                  className="relative h-12 ml-4 text-[18px] transition-all duration-300 hover:scale-105 z-10"
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!enablingSystemOpen) {
                        setEnablingSystemOpen(true);
                      } else {
                        navigate("/EnablingSystem");
                      }
                    }}
                    className="w-full text-text-primary font-bold focus:outline-none hover:text-primary transition-colors"
                  >
                    <h3>{t("ConditionsElements.name")}</h3>
                  </button>
                  {enablingSystemOpen && (
                    <div className="flex glass-card text-text-primary shadow-glass rounded-xl p-2 z-40 w-34 justify-center max-w-xs sm:max-w-sm md:max-w-md break-words"
                    >
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <li>
                          <Link
                            to="/Frameworks"
                            className="hover:text-Frameworks"
                          >
                            {t("ConceptualFrameworks.title")}
                          </Link>
                        </li>
                        <li>
                          <Link to="/EconomyMarket" className="hover:text-Market">
                            {t("EconomicsandMarketForces.title")}
                          </Link>
                        </li>
                        <li>
                          <Link to="/Metrics" className="hover:text-Metrics">
                            {t("Metrics.title")}
                          </Link>
                        </li>
                        <li>
                          <Link to="/Policies" className="hover:text-Policie">
                            {t("PoliciesandRegulations.title")}
                          </Link>
                        </li>
                        <li>
                          <Link to="/Tools" className="hover:text-Tools">
                            {t("Tools.title")}
                          </Link>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                // Desktop
                <div className="relative w-1/4 group h-12 lg transition-all duration-300 hover:scale-105 z-10">
                  <Link to="/EnablingSystem" className="text-text-primary hover:text-primary transition-colors">
                    <h3>{t("ConditionsElements.name")}</h3>
                  </Link>
                  <div
                    className={`absolute hidden group-hover:block glass-card text-text-primary shadow-glass rounded-xl p-4 z-50 break-words ${isLargeScreen ? "w-full max-w-xl" : "w-[90%] max-w-xs sm:max-w-sm md:max-w-md"}`}
                  >
                    <ul className={`grid gap-4 text-base h-72 ${isLargeScreen ? "grid-cols-1" : "sm:grid-cols-2"}`}
                    >
                      <li>
                        <Link to="/Frameworks" className="hover:text-Frameworks">
                          {t("ConceptualFrameworks.title")}
                        </Link>
                      </li>
                      <li>
                        <Link to="/EconomyMarket" className="hover:text-Market">
                          {t("EconomicsandMarketForces.title")}
                        </Link>
                      </li>
                      <li>
                        <Link to="/Metrics" className="hover:text-Metrics">
                          {t("Metrics.title")}
                        </Link>
                      </li>
                      <li>
                        <Link to="/Policies" className="hover:text-Policie">
                          {t("PoliciesandRegulations.title")}
                        </Link>
                      </li>
                      <li>
                        <Link to="/Tools" className="hover:text-Tools">
                          {t("Tools.title")}
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              )}  
              <Link
                to="/NobleElements"
                className="w-1/12 xs:text-[18px] xs:ml-5 xl:text-lg transition-all duration-300 hover:scale-105 text-text-primary hover:text-primary"
              > 
                <h3>{t("NobleElements.name")}</h3>
              </Link>
            </div>
            <div className={`${screenWidth <= 800 ? "pt-5" : "pt-0"} min-w-max`}>
              <div className="flex flex-nowrap w-full pl-[5px] justify-start break-words text-center xs:text-[8px] p:text-[9px] gg:text-[10px] xg:text-[11px]"
              >
                {renderColumn(elements,0,7,false,0,handleElementClick,setSelectedElement,false,false,false,false,true,windowSize,t )}
                {renderColumn(elements,7,13,true,1,handleElementClick,setSelectedElement,false,false,false,false,true,windowSize,t )}
                {renderColumn(elements,13,17,true,3,handleElementClick,setSelectedElement,false,false,false,false,true,windowSize,t )}
                {renderColumn(elements,17,21,true,3,handleElementClick,setSelectedElement,false,false,false,false,true,windowSize,t )}
                {renderColumn(elements,21,25,true,3,handleElementClick,setSelectedElement,false,false,false,false,true,windowSize,t )}
                {renderColumn(elements,25,29,true,3,handleElementClick,setSelectedElement,false,false,false,false,true,windowSize,t )}
                {renderColumn(elements,29,33,true,3,handleElementClick,setSelectedElement,false,false,false,false,true,windowSize,t )}
                {renderColumn(elements,33,37,true,3,handleElementClick,setSelectedElement,false,false,false,false,true,windowSize,t )}
                {renderColumn(elements,37,41,true,3,handleElementClick,setSelectedElement,false,false,false,false,true,windowSize,t )}
                {renderColumn(elements,41,45,true,3,handleElementClick,setSelectedElement,false,false,false,false,true,windowSize,t )}
                {renderColumn(elements,45,49,true,3,handleElementClick,setSelectedElement,false,false,false,false,true,windowSize,t )}
                {renderColumn(elements,49,53,true,3,handleElementClick,setSelectedElement,false,false,false,false,true,windowSize,t )}
                {renderColumn(elements,53,59,true,1,handleElementClick,setSelectedElement,false,false,false,false,true,windowSize,t )}
                {renderColumn(elements,59,65,true,1,handleElementClick,setSelectedElement,false,false,false,false,true,windowSize,t )}
                {renderColumn(elements,65,71,true,1,handleElementClick,setSelectedElement,false,false,false,false,true,windowSize,t )}
                {renderColumn(elements,71,77,true,1,handleElementClick,setSelectedElement,false,false,false,false,true,windowSize,t )}
                {renderColumn(elements,77,83,true,1,handleElementClick,setSelectedElement,false,false,false,false,true,windowSize,t )}
              {renderColumn(elements,83,90, true,0,handleElementClick,setSelectedElement,false,false,false,false,true,windowSize,t )}
              </div>
            </div>
            </div>
            <div className="flex flex-col items-center mt-10">
              <h1 className="text-center text-xl text-text-primary font-bold">{t('TpqvsLibras.changeHome')}</h1>
              <button
                onClick={() => navigate('/')} 
                className="btn-primary mb-4 mt-4"
              >  
                {t('TpqvsLibras.title')}
              </button>
            </div>
          </div>
      )}
    </section>
  );
}

export default TablePeriodic;
